import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Mascot from "../components/Mascot";
import { Shield, Magnet, Timer, Award } from "lucide-react";

const RARITY_CLASS = {
  common: "rarity-common",
  rare: "rarity-rare",
  epic: "rarity-epic",
  legendary: "rarity-legendary",
};

const POWERUP_ICONS = {
  shield: Shield,
  magnet: Magnet,
  slowmo: Timer,
};

export default function Shop() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("skins");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/shop/items");
    setItems(data.items || []);
  };

  useEffect(() => {
    load();
  }, []);

  const buy = async (it) => {
    setBusy(it.item_id);
    try {
      await api.post("/shop/purchase", { item_id: it.item_id });
      toast.success(`Purchased ${it.name}!`);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const equip = async (it) => {
    setBusy(it.item_id);
    try {
      await api.post("/shop/equip", { item_id: it.item_id });
      toast.success(`Equipped ${it.name}`);
      await refreshUser();
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const skins = items.filter((i) => i.type === "skin");
  const powerups = items.filter((i) => i.type === "power_up");
  const certificates = items.filter((i) => i.type === "certificate");

  return (
    <Layout>
      <div className="px-4 py-5 flex flex-col gap-4" data-testid="shop-page">
        <div className="flex items-center justify-between">
          <h1 className="font-pixel text-[14px] text-glow-green">◆ SHOP</h1>
          <div className="cb-pill border-[rgba(247,147,26,0.5)] text-[#f7931a] bg-[rgba(247,147,26,0.08)]">
            {user?.boom_credits ?? 0} BC
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setTab("skins")}
            data-testid="shop-tab-skins"
            className={`cb-pill flex-1 ${tab === "skins" ? "bg-[#2ecc71] text-[#0f0f1e]" : ""}`}
          >
            SKINS
          </button>
          <button
            onClick={() => setTab("powerups")}
            data-testid="shop-tab-powerups"
            className={`cb-pill flex-1 ${tab === "powerups" ? "bg-[#f7931a] text-[#0f0f1e]" : ""}`}
          >
            POWER-UPS
          </button>
          {certificates.length > 0 && (
            <button
              onClick={() => setTab("certificates")}
              data-testid="shop-tab-certificates"
              className={`cb-pill flex-1 ${tab === "certificates" ? "bg-[#2e75b6] text-[#0f0f1e]" : ""}`}
            >
              CERTIFICATES
            </button>
          )}
        </div>

        {tab === "skins" && (
          <div className="grid grid-cols-2 gap-3">
            {skins.map((it) => (
              <div
                key={it.item_id}
                className={`p-3 rounded-[8px] border-2 bg-[rgba(15,15,30,0.7)] flex flex-col items-center gap-2 ${RARITY_CLASS[it.rarity]}`}
                data-testid={`shop-item-${it.item_id}`}
              >
                <Mascot size={68} floating={false} skin={it.image} />
                <div className="font-pixel text-[10px] text-white text-center">
                  {it.name}
                </div>
                <div className="text-[9px] uppercase font-pixel opacity-70">
                  {it.rarity}
                </div>
                <div className="text-[10px] text-[#f7931a] font-pixel">
                  {it.price_bc} BC
                </div>
                <SkinAction
                  item={it}
                  busy={busy === it.item_id}
                  canAfford={(user?.boom_credits ?? 0) >= it.price_bc}
                  onBuy={() => buy(it)}
                  onEquip={() => equip(it)}
                />
              </div>
            ))}
          </div>
        )}

        {tab === "powerups" && (
          <div className="flex flex-col gap-3">
            {powerups.map((it) => {
              const Icon = POWERUP_ICONS[it.power_up] || Shield;
              const canAfford = (user?.boom_credits ?? 0) >= it.price_bc;
              return (
                <div
                  key={it.item_id}
                  className={`p-4 rounded-[8px] border-2 bg-[rgba(15,15,30,0.7)] flex items-center gap-3 ${RARITY_CLASS[it.rarity]}`}
                  data-testid={`shop-item-${it.item_id}`}
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[rgba(247,147,26,0.1)] border border-[rgba(247,147,26,0.35)] flex-shrink-0">
                    <Icon size={22} className="text-[#f7931a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-pixel text-[10px] text-white mb-1">{it.name}</div>
                    <div className="text-[10px] text-[#8b95a9]">{it.description}</div>
                    <div className="text-[10px] text-[#2ecc71] font-mono mt-1" data-testid={`shop-stock-${it.item_id}`}>
                      In stock: {it.owned_count ?? 0}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-[10px] text-[#f7931a] font-pixel">{it.price_bc} BC</div>
                    <button
                      onClick={() => buy(it)}
                      disabled={busy === it.item_id || !canAfford}
                      data-testid={`shop-buy-${it.item_id}`}
                      className="cb-neon-btn orange text-[9px] py-2 px-3"
                    >
                      {busy === it.item_id ? "..." : canAfford ? "Buy" : "Low BC"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "certificates" && (
          <div className="flex flex-col gap-3">
            {certificates.map((it) => (
              <div
                key={it.item_id}
                className={`p-4 rounded-[8px] border-2 bg-[rgba(15,15,30,0.7)] flex items-center gap-3 ${RARITY_CLASS[it.rarity]}`}
                data-testid={`shop-item-${it.item_id}`}
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[rgba(46,117,182,0.1)] border border-[rgba(46,117,182,0.35)] flex-shrink-0">
                  <Award size={22} className="text-[#2e75b6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-pixel text-[10px] text-white mb-1">{it.name}</div>
                  <div className="text-[10px] text-[#8b95a9]">{it.description}</div>
                </div>
                <div
                  className="cb-pill border-[rgba(46,117,182,0.5)] text-[#2e75b6]"
                  data-testid={`shop-status-${it.item_id}`}
                >
                  {user?.academy_certificate ? "OWNED" : "EARN IT"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SkinAction({ item, busy, canAfford, onBuy, onEquip }) {
  if (item.owned && item.equipped) {
    return (
      <button
        disabled
        className="cb-pill w-full text-center"
        data-testid={`shop-equipped-${item.item_id}`}
      >
        EQUIPPED
      </button>
    );
  }
  if (item.owned) {
    return (
      <button
        onClick={onEquip}
        disabled={busy}
        data-testid={`shop-equip-${item.item_id}`}
        className="cb-neon-btn blue w-full text-[9px] py-2"
      >
        Equip
      </button>
    );
  }
  return (
    <button
      onClick={onBuy}
      disabled={busy || !canAfford}
      data-testid={`shop-buy-${item.item_id}`}
      className="cb-neon-btn w-full text-[9px] py-2"
    >
      Buy
    </button>
  );
}
